import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MoveToInboxRoundedIcon from '@mui/icons-material/MoveToInboxRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import { Alert, Box, Button, Snackbar, Stack, Tab, Tabs } from '@mui/material';

import { ErrorState, PageContainer } from '@/shared/ui';

import { useInventoryPermissions } from '../../lib/useInventoryPermissions';
import { useToast } from '../../lib/useToast';
import { AdjustStockDialog } from '../components/AdjustStockDialog';
import { AlertsPanel } from '../components/AlertsPanel';
import { LedgerPanel } from '../components/LedgerPanel';
import { ReceiveStockDialog } from '../components/ReceiveStockDialog';
import { StockPanel } from '../components/StockPanel';
import type { StockPolicyTarget } from '../components/StockPolicyDialog';
import { StockPolicyDialog } from '../components/StockPolicyDialog';
import { TransferStockDialog } from '../components/TransferStockDialog';

type TabKey = 'stock' | 'ledger' | 'alerts';

interface LedgerSeed {
  productId?: string;
  locationId?: string;
}

/**
 * Inventory — stock levels, movements and alerts across the tenant's fulfilment locations.
 * Mirrors `PricingPage`'s shape: one page, several tabbed panels for one rich domain.
 */
export function InventoryPage() {
  const { t } = useTranslation('inventory');
  const { canView, canManage } = useInventoryPermissions();
  const { toast, success, error: notifyError, clear } = useToast();

  const [tab, setTab] = useState<TabKey>('stock');
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [policyTarget, setPolicyTarget] = useState<StockPolicyTarget | null>(null);
  const [ledgerSeed, setLedgerSeed] = useState<LedgerSeed>({});
  const [ledgerSeedVersion, setLedgerSeedVersion] = useState(0);

  // plan-platform-001 WP2.3 (design-stock-channels-page §12.1): the channel-stock page's derivation
  // drill-down deep-links here as /inventory?tab=ledger&productId=&locationId= — read once on mount
  // into the existing ledger-seed mechanism.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('tab') === 'ledger') {
      const productId = searchParams.get('productId') ?? undefined;
      const locationId = searchParams.get('locationId') ?? undefined;
      setLedgerSeed({ productId, locationId });
      setLedgerSeedVersion((version) => version + 1);
      setTab('ledger');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onResult = (severity: 'success' | 'error' | 'info' | 'warning', message: string) =>
    severity === 'success' ? success(message) : notifyError(message);

  const openLedgerFor = (productId: string, locationId: string) => {
    setLedgerSeed({ productId, locationId });
    setLedgerSeedVersion((v) => v + 1);
    setTab('ledger');
  };

  const openPolicyFor = (productId: string, locationId: string) => {
    setPolicyTarget({ productId, locationId });
  };

  if (!canView) {
    return (
      <PageContainer title={t('page.title')}>
        <ErrorState
          title={t('page.accessRestricted.title')}
          message={t('page.accessRestricted.message')}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={t('page.title')}
      subtitle={t('page.subtitle')}
      actions={
        canManage ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<MoveToInboxRoundedIcon />}
              onClick={() => setReceiveOpen(true)}
            >
              {t('page.actions.receive')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<EditNoteRoundedIcon />}
              onClick={() => setAdjustOpen(true)}
            >
              {t('page.actions.adjust')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<SwapHorizRoundedIcon />}
              onClick={() => setTransferOpen(true)}
            >
              {t('page.actions.transfer')}
            </Button>
          </Stack>
        ) : undefined
      }
    >
      {!canManage ? (
        <Alert severity="info" icon={<LockOutlinedIcon fontSize="inherit" />} sx={{ mb: 2.5 }}>
          {t('page.readOnlyNotice')}
        </Alert>
      ) : null}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2.5 }}>
        <Tabs
          value={tab}
          onChange={(_e, value: TabKey) => setTab(value)}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          <Tab value="stock" label={t('page.tabs.stock')} />
          <Tab value="ledger" label={t('page.tabs.ledger')} />
          <Tab value="alerts" label={t('page.tabs.alerts')} />
        </Tabs>
      </Box>

      {tab === 'stock' ? (
        <StockPanel onOpenLedger={openLedgerFor} onOpenPolicy={openPolicyFor} />
      ) : null}
      {tab === 'ledger' ? (
        <LedgerPanel
          key={ledgerSeedVersion}
          initialProductId={ledgerSeed.productId}
          initialLocationId={ledgerSeed.locationId}
        />
      ) : null}
      {tab === 'alerts' ? <AlertsPanel onOpenPolicy={openPolicyFor} /> : null}

      <ReceiveStockDialog
        open={receiveOpen}
        onClose={() => setReceiveOpen(false)}
        onResult={onResult}
      />
      <AdjustStockDialog
        open={adjustOpen}
        onClose={() => setAdjustOpen(false)}
        onResult={onResult}
      />
      <TransferStockDialog
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        onResult={onResult}
      />
      <StockPolicyDialog
        target={policyTarget}
        canManage={canManage}
        onClose={() => setPolicyTarget(null)}
        onResult={onResult}
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={clear}
          severity={toast?.severity ?? 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}
